import React from 'react';
import type { IconProps } from '@atlaskit/icon';
import ArchiveBoxIcon from '@atlaskit/icon/core/archive-box';
import ComponentIcon from '@atlaskit/icon/core/component';
import InboxIcon from '@atlaskit/icon/core/inbox';
import FormIcon from '@atlaskit/icon/core/form';
import GlobeIcon from '@atlaskit/icon/core/globe';
import GoalIcon from '@atlaskit/icon/core/goal';
import IncidentIcon from '@atlaskit/icon/core/incident';
import LightbulbIcon from '@atlaskit/icon/core/lightbulb';
import LinkIcon from '@atlaskit/icon/core/link';
import AngleBracketsIcon from '@atlaskit/icon/core/migration/angle-brackets--code';
import AttachmentsIcon from '@atlaskit/icon/core/migration/attachment';
import BacklogIcon from '@atlaskit/icon/core/migration/backlog';
import BoardIcon from '@atlaskit/icon/core/migration/board';
import CalendarIcon from '@atlaskit/icon/core/migration/calendar';
import ChartTrendIcon from '@atlaskit/icon/core/migration/chart-trend--graph-line';
import CloudArrowUpIcon from '@atlaskit/icon/core/migration/cloud-arrow-up--upload';
import DecisionIcon from '@atlaskit/icon/core/migration/decision--editor-decision';
import IssuesIcon from '@atlaskit/icon/core/migration/issues';
import PageIcon from '@atlaskit/icon/core/migration/page';
import ReleaseIcon from '@atlaskit/icon/core/migration/release--ship';
import RoadmapIcon from '@atlaskit/icon/core/migration/roadmap';
import OnCallIcon from '@atlaskit/icon/core/on-call';
import ShieldIcon from '@atlaskit/icon/core/shield';
import SpreadsheetIcon from '@atlaskit/icon/core/spreadsheet';
import BulletListOld from '@atlaskit/icon/glyph/bullet-list';
import ShortcutsIcon from '@atlaskit/icon/glyph/editor/link';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import PhoneIcon from '@atlaskit/icon/glyph/hipchat/dial-out';
import { fg } from '@atlassian/jira-feature-gating';
import AppPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/app-preview/index.tsx';
import ApprovalsPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/approvals-preview/index.tsx';
import AttachmentsPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/attachments-preview/index.tsx';
import BacklogPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/backlog-preview/index.tsx';
import BoardPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/board-preview/index.tsx';
import CalendarPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/calendar-preview/index.tsx';
import CodePreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/code-preview/index.tsx';
import ComponentPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/component-preview/index.tsx';
import DeploymentsPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/deployments-preview/index.tsx';
import DevelopmentPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/development-preview/index.tsx';
import FormsPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/forms-preview/index.tsx';
import GoalsPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/goals-preview/index.tsx';
import IncidentsPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/incidents-preview/index.tsx';
import IssuesPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/issues-preview/index.tsx';
import ListPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/list-preview/index.tsx';
import OnCallPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/oncall-preview/index.tsx';
import PagesPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/pages-preview/index.tsx';
import ReleasesPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/releases-preview/index.tsx';
import ReportsPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/reports-preview/index.tsx';
import SecurityPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/security-preview/index.tsx';
import ShortcutsPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/shortcuts-preview/index.tsx';
import SummaryPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/summary-preview/index.tsx';
import TimelinePreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/timeline-preview/index.tsx';
import type { TabsDescriptor } from '@atlassian/jira-horizontal-nav-common/src/common/types.tsx';
import { GoalIcon as GoalIconOld } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/goal-icon/index.tsx';
import messages from './messages.tsx';

export const JIRA_TABS: TabsDescriptor = {
	APP: {
		Icon: (props: IconProps) => (
			<DecisionIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={props.size}
			/>
		),
		Illustration: AppPreview,
		description: () => messages.appDescription,
	},
	APPROVALS: {
		Icon: (props: IconProps) => (
			<AttachmentsIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={props.size}
			/>
		),
		Illustration: ApprovalsPreview,
		description: () => messages.approvalsDescription,
	},
	ARCHIVED_ISSUES: {
		Icon: (props: IconProps) => (
			<ArchiveBoxIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={props.size}
			/>
		),
		Illustration: IssuesPreview,
		description: () =>
			fg('jira-issue-terminology-refresh-m3')
				? messages.archivedIssuesDescriptionIssueTermRefresh
				: messages.archivedIssuesDescription,
	},
	ATTACHMENTS: {
		Icon: (props: IconProps) => (
			<BacklogIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={props.size}
			/>
		),
		Illustration: AttachmentsPreview,
		description: () =>
			fg('jira-issue-terminology-refresh-m3')
				? messages.attachmentsDescriptionIssueTermRefresh
				: messages.attachmentsDescription,
	},
	BACKLOG: {
		Icon: (props: IconProps) => (
			<BacklogIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={props.size}
			/>
		),
		Illustration: BacklogPreview,
		description: () => messages.backlogDescription,
	},
	BOARD: {
		Icon: (props: IconProps) => (
			<BoardIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={props.size}
			/>
		),
		Illustration: BoardPreview,
		description: () => messages.boardDescription,
	},
	CALENDAR: {
		Icon: (props: IconProps) => (
			<CalendarIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={props.size}
			/>
		),
		Illustration: CalendarPreview,
		description: () => messages.calendarDescription,
	},
	CODE: {
		Icon: (props: IconProps) => (
			<AngleBracketsIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={props.size}
			/>
		),
		Illustration: CodePreview,
		description: () => messages.codeDescription,
	},
	COMPONENTS: {
		Icon: (props: IconProps) => (
			<ComponentIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={props.size}
			/>
		),
		Illustration: ComponentPreview,
		description: () => messages.componentsDescription,
	},
	DEPLOYMENTS: {
		Icon: (props: IconProps) => (
			<CloudArrowUpIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={props.size}
			/>
		),
		Illustration: DeploymentsPreview,
		description: () => messages.deploymentsDescription,
	},
	DEVELOPMENT: {
		Icon: (props: IconProps) => (
			<LightbulbIcon
				label=""
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={props.size}
			/>
		),
		Illustration: DevelopmentPreview,
		description: () => messages.developmentDescription,
	},
	FORMS: {
		Icon: (props: IconProps) => (
			<FormIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={props.size}
			/>
		),
		Illustration: FormsPreview,
		description: () => messages.formsDescription,
	},
	GOALS: {
		Icon: (props: IconProps) => (
			<GoalIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={props.size}
				LEGACY_fallbackIcon={GoalIconOld}
			/>
		),
		Illustration: GoalsPreview,
		description: () =>
			fg('jira-issue-terminology-refresh-m3')
				? messages.goalsDescriptionIssueTermRefresh
				: messages.goalsDescription,
	},
	INCIDENTS: {
		Icon: (props: IconProps) => (
			<IncidentIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={props.size}
				LEGACY_fallbackIcon={ErrorIcon}
			/>
		),
		Illustration: IncidentsPreview,
		description: () => messages.incidentsDescription,
	},
	ISSUES: {
		Icon: (props: IconProps) => (
			<IssuesIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={props.size}
			/>
		),
		Illustration: IssuesPreview,
		description: () =>
			fg('jira-issue-terminology-refresh-m3')
				? messages.issuesDescriptionIssueTermRefresh
				: messages.issuesDescription,
	},
	LIST: {
		Icon: (props: IconProps) => (
			<SpreadsheetIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={props.size}
				LEGACY_fallbackIcon={BulletListOld}
			/>
		),
		Illustration: ListPreview,
		description: () => messages.listDescription,
	},
	ON_CALL: {
		Icon: (props: IconProps) => (
			<OnCallIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={props.size}
				LEGACY_fallbackIcon={PhoneIcon}
			/>
		),
		Illustration: OnCallPreview,
		description: () => messages.onCallDescription,
	},
	PAGES: {
		Icon: (props: IconProps) => (
			<PageIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={props.size}
			/>
		),
		Illustration: PagesPreview,
		description: () => messages.pagesDescription,
	},
	RELEASES: {
		Icon: (props: IconProps) => (
			<ReleaseIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={props.size}
			/>
		),
		Illustration: ReleasesPreview,
		description: () => messages.releasesDescription,
	},
	REPORTS: {
		Icon: (props: IconProps) => (
			<ChartTrendIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={props.size}
			/>
		),
		Illustration: ReportsPreview,
		description: () =>
			fg('jira-issue-terminology-refresh-m3')
				? messages.reportsDescriptionIssueTermRefresh
				: messages.reportsDescription,
	},
	SECURITY: {
		Icon: (props: IconProps) => (
			<ShieldIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={props.size}
			/>
		),
		Illustration: SecurityPreview,
		description: () => messages.securityDescription,
	},
	SHORTCUTS: {
		Icon: (props: IconProps) => (
			<LinkIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={props.size}
				LEGACY_fallbackIcon={ShortcutsIcon}
			/>
		),
		Illustration: ShortcutsPreview,
		description: () => messages.shortcutsDescription,
	},
	SUMMARY: {
		Icon: (props: IconProps) => (
			<GlobeIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={props.size}
			/>
		),
		Illustration: SummaryPreview,
		description: () => messages.summaryDescriptionNonFinal,
	},
	TIMELINE: {
		Icon: (props: IconProps) => (
			<RoadmapIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={props.size}
			/>
		),
		Illustration: TimelinePreview,
		description: () => messages.timelineDescription,
	},
	INBOX: {
		Icon: (props: IconProps) => (
			<InboxIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={props.size}
			/>
		),
		Illustration: IssuesPreview,
		description: () => messages.inboxDescription,
	},
};
